import { alpha, Theme } from '@mui/material/styles'

const leftBandStyles = {
  content: '""',
  position: 'absolute',
  top: 0,
  left: 0,
  width: 0,
  height: '100%',
}

const useStyles = {
  errorContainer: (theme: Theme) => ({
    marginTop: theme.spacing(3),
  }),
  dataGrid: (theme: Theme) => ({
    '& .MuiDataGrid-cell': {
      overflow: 'unset',
    },
    '& .MuiDataGrid-row:last-child': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiDataGrid-columnHeaders': {
      border: 0,
      paddingLeft: theme.spacing(1),
    },
    '& .MuiCheckbox-root.Mui-disabled': {
      opacity: 0,
    },
  }),
  gridToolbarTitleContainer: {
    padding: '0 10px',
  },
  gridToolbarCheckbox: {
    marginLeft: '6px',
  },
  gridBaseCheckbox: (theme: Theme) => ({
    alignSelf: 'flex-start',
    padding: 0,
    color: theme.palette.divider,
  }),
  dataGridRow: {
    '& .MuiDataGrid-cell': {
      alignItems: 'flex-start',
      padding: '13px 10px',
    },
  },
  deviceStatusChip: (theme: Theme) => ({
    flex: 1,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      flex: 'none',
      maxWidth: 'none',
    },
  }),
  deviceStatusChipCheckbox: (theme: Theme) => ({
    [theme.breakpoints.down('md')]: {
      flex: 1,
      maxWidth: 'calc(100% + 32px)',
      marginLeft: theme.spacing(-2),
    },
  }),
  nameLink: (theme: Theme) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: theme.typography.body1.fontSize,
    marginRight: theme.spacing(1),
  }),
  metaTag: (theme: Theme) => ({
    color: theme.palette.text.primary,
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '50px',
    padding: theme.spacing(0, 1),
    whiteSpace: 'nowrap',
    display: 'inline-block',
    marginRight: '5px',
  }),
  locationNotes: {
    overflow: 'hidden',
    maxHeight: '20px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  moreOptionsBtnSelected: (theme: Theme) => ({
    backgroundColor: theme.palette.secondary.shades['4p'],
  }),
  rowBandFirstOfType: (theme: Theme) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    '&:after': {
      borderRadius: theme.spacing(0.5, 0, 0, 0),
    },
  }),
  rowBandLastOfType: (theme: Theme) => ({
    '&:after': {
      borderRadius: theme.spacing(0, 0, 0, 0.5),
    },
  }),
  rowBandOnlyOfType: (theme: Theme) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    '&:after': {
      borderRadius: theme.spacing(0.5, 0, 0, 0.5),
    },
  }),
  rowBandRed: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid ${theme.palette.error.main}`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.palette.error.main} 7px, ${alpha(
        theme.palette.error.main ?? '',
        0.6
      )}) border-box`,
    },
  }),
  rowBandOrange: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid ${theme.palette.warning.main}`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.palette.warning.main} 7px, ${alpha(
        theme.palette.warning.main ?? '',
        0.6
      )}) border-box`,
    },
  }),
  rowBandPrimaryBlue: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid ${theme.palette.primary.main}`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.palette.primary.main} 7px, ${alpha(
        theme.palette.primary.main ?? '',
        0.6
      )}) border-box`,
    },
  }),
  rowBandGreen: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid ${theme.palette.success.main}`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.palette.success.main} 7px, ${alpha(
        theme.palette.success.main ?? '',
        0.6
      )}) border-box`,
    },
  }),
  rowBandGray: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid #BDBDBD`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, #BDBDBD 7px, ${alpha('#BDBDBD', 0.6)}) border-box`,
    },
  }),
  rowBandBlue1: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid ${theme.palette.blue.variant1}`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.palette.blue.variant1} 7px, ${alpha(
        theme.palette.blue.variant1 ?? '',
        0.6
      )}) border-box`,
    },
  }),
  rowBandBlue2: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid ${theme.palette.blue.variant2}`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.palette.blue.variant2} 7px, ${alpha(
        theme.palette.blue.variant2 ?? '',
        0.6
      )}) border-box`,
    },
  }),
  rowBandBlue3: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid ${theme.palette.blue.variant3}`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.palette.blue.variant3} 7px, ${alpha(
        theme.palette.blue.variant3 ?? '',
        0.6
      )}) border-box`,
    },
  }),
  rowBandBlue4: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid ${theme.palette.blue.variant4}`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.palette.blue.variant4} 7px, ${alpha(
        theme.palette.blue.variant4 ?? '',
        0.6
      )}) border-box`,
    },
  }),
  rowBandBlue5: (theme: Theme) => ({
    position: 'relative',
    '&:after': {
      ...leftBandStyles,
      borderLeft: `${theme.spacing(1)} solid ${theme.palette.blue.variant5}`,
      background: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.palette.blue.variant5} 7px, ${alpha(
        theme.palette.blue.variant5 ?? '',
        0.6
      )}) border-box`,
    },
  }),
  gatewayDevice: (theme: Theme) => ({
    '&:after': {
      borderLeft: `${theme.spacing(2)} solid transparent`,
    },
  }),
  gatewayDeviceActionMode: (theme: Theme) => ({
    '& .MuiDataGrid-cellCheckbox': {
      marginLeft: theme.spacing(2),
      paddingRight: theme.spacing(3),
    },
  }),
  groupRow: {
    '& .MuiDataGrid-cellCheckbox span': {
      opacity: 0,
    },
    '&:after': {
      borderLeft: 0,
    },
  },
  hideCheckbox: {
    opacity: 0,
  },
  collapsedRow: {
    '&.MuiDataGrid-row': {
      display: 'none',
    },
  },
  offlineGatewayAlert: (theme: Theme) => ({
    backgroundColor: '#FFCC69',
    borderRadius: theme.spacing(0, 0, 0, 0.5),
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    '& .MuiAlert-message': {
      padding: '1px',
    },
    '& a.MuiLink-root': {
      fontWeight: 'bold',
    },
  }),
  vanityNameContainer: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  groupAccordionContainer: {
    marginTop: '-13px',
  },
  groupAccordion: {
    position: 'absolute',
    left: 0,
    padding: '1px 0 2px 0',
    margin: 0,
    width: '100%',
    boxShadow: 'none',
  },
  groupAccordionSummary: {
    padding: '0 0 0 20px',
    margin: 0,
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
  },
  hideOnSmallScreen: (theme: Theme) => ({
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  }),
}
export default useStyles
